import { useState } from "react";
import AppSelect from "../AppSelect";
import { apiFetch } from "../../../apiClient";
import FormModal, {
  FormActions,
  FormError,
  FormField,
  FormSection,
  formInputClass,
} from "../FormModal";

type AddLeadDivProps = {
  onClose: () => void;
  preassignedTo?: string;
};

const TIPO_META: Record<string, { label: string; color: string }> = {
  contatto: { label: "LinkedIn", color: "bg-sky-500/15 text-sky-800 ring-sky-200" },
  "small-lead": { label: "Small Lead", color: "bg-amber-500/15 text-amber-800 ring-amber-200" },
  startup: { label: "Start-up", color: "bg-violet-500/15 text-violet-800 ring-violet-200" },
};

const LeadIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

export default function AddLeadDiv({ onClose, preassignedTo }: AddLeadDivProps) {
  const [tipo, setTipo] = useState<{ value: string; label: string } | null>({
    value: "contatto",
    label: "Contatto LinkedIn",
  });
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [settore, setSettore] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [azienda, setAzienda] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tipoOptions = [
    { value: "contatto", label: "Contatto LinkedIn" },
    { value: "small-lead", label: "Small Lead" },
    { value: "startup", label: "Startup Toscana" },
  ];

  const currentTipo = tipo?.value;
  const tipoMeta = currentTipo ? TIPO_META[currentTipo] : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (currentTipo === "contatto" && (!nome.trim() || !cognome.trim())) {
      setError("Nome e Cognome sono obbligatori per un Contatto LinkedIn");
      return;
    }

    if (currentTipo === "startup" && !azienda.trim() && !nome.trim()) {
      setError("Inserisci almeno il Nome Azienda o il Nome del referente per la Startup");
      return;
    }

    if (currentTipo === "small-lead" && !nome.trim()) {
      setError("Il campo Nome è obbligatorio per gli Small Lead");
      return;
    }

    const payload: Record<string, string | null> = {
      tipo: currentTipo ?? null,
      nome: nome.trim() || null,
      settore: settore.trim() || null,
      indirizzo: indirizzo.trim() || null,
    };

    if (preassignedTo) {
      payload.responsabile_jevis = preassignedTo;
    }

    if (currentTipo === "contatto") {
      payload.cognome = cognome.trim() || null;
      payload.linkedin_url = linkedinUrl.trim() || null;
      payload.azienda = azienda.trim() || null;
    } else if (currentTipo === "small-lead") {
      payload.telefono = telefono.trim() || null;
      payload.email = email.trim() || null;
    } else if (currentTipo === "startup") {
      payload.cognome = cognome.trim() || null;
      payload.linkedin_url = linkedinUrl.trim() || null;
      payload.azienda = azienda.trim() || null;
      payload.telefono = telefono.trim() || null;
      payload.email = email.trim() || null;
    }

    try {
      setLoading(true);

      const res = await apiFetch("createLead/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data: { detail?: string } = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || JSON.stringify(data));
        return;
      }

      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message);
      else setError("Errore imprevisto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      eyebrow="Nuovo record"
      title="Aggiungi lead / contatto"
      description="Scegli il tipo di canale e compila i campi richiesti."
      icon={<LeadIcon />}
      onClose={onClose}
      size="lg"
      footer={
        <FormActions
          onCancel={onClose}
          submitLabel="Salva record"
          loading={loading}
          loadingLabel="Salvataggio…"
          formId="add-lead-form"
        />
      }
    >
      <form id="add-lead-form" onSubmit={handleSubmit} className="space-y-5">
        <FormField label="Tipo di lead">
          <AppSelect
            options={tipoOptions}
            value={tipo}
            onChange={(option) => {
              setTipo(option);
              setError("");
            }}
          />
          {tipoMeta ? (
            <span className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${tipoMeta.color}`}>
              {tipoMeta.label}
            </span>
          ) : null}
        </FormField>

        {preassignedTo ? (
          <div className="rounded-xl border border-secondary/15 bg-secondary/5 px-3 py-2.5 text-sm">
            <span className="font-semibold text-secondary">Assegnato a:</span>{" "}
            <span className="text-secondary/80">{preassignedTo}</span>
          </div>
        ) : null}

        <FormSection title="Anagrafica">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={currentTipo === "startup" ? "Nome referente" : "Nome"} required={currentTipo !== "startup"}>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder={currentTipo === "startup" ? "Nome referente" : "Nome"}
                className={formInputClass}
              />
            </FormField>

            {(currentTipo === "contatto" || currentTipo === "startup") && (
              <FormField label={currentTipo === "startup" ? "Cognome referente" : "Cognome"} required={currentTipo === "contatto"}>
                <input
                  type="text"
                  value={cognome}
                  onChange={(e) => setCognome(e.target.value)}
                  placeholder="Cognome"
                  className={formInputClass}
                />
              </FormField>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Settore / Core business">
              <input
                type="text"
                value={settore}
                onChange={(e) => setSettore(e.target.value)}
                placeholder="Es. Software, Edilizia…"
                className={formInputClass}
              />
            </FormField>
            <FormField label="Sede / Indirizzo">
              <input
                type="text"
                value={indirizzo}
                onChange={(e) => setIndirizzo(e.target.value)}
                placeholder="Città o indirizzo"
                className={formInputClass}
              />
            </FormField>
          </div>
        </FormSection>

        {(currentTipo === "contatto" || currentTipo === "startup") && (
          <FormSection title="Azienda e social">
            <FormField label={currentTipo === "startup" ? "Ragione sociale / Azienda" : "Nome azienda"}>
              <input
                type="text"
                value={azienda}
                onChange={(e) => setAzienda(e.target.value)}
                placeholder="Nome azienda"
                className={formInputClass}
              />
            </FormField>
            <FormField label="LinkedIn URL">
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/…"
                className={formInputClass}
              />
            </FormField>
          </FormSection>
        )}

        {(currentTipo === "small-lead" || currentTipo === "startup") && (
          <FormSection title="Contatti">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Telefono">
                <input
                  type="text"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="+39 …"
                  className={formInputClass}
                />
              </FormField>
              <FormField label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@azienda.it"
                  className={formInputClass}
                />
              </FormField>
            </div>
          </FormSection>
        )}

        <FormError message={error} />
      </form>
    </FormModal>
  );
}
